import { createContext, useContext, useRef, useState, useCallback } from 'react';
import { F } from '../../theme.jsx';
import { useModalViewport, modalShellStyle } from '../../modal-utils.jsx';

/**
 * ConfirmProvider + useConfirm
 *
 * Provides a global imperative confirm dialog.
 *
 * Setup: wrap your app in <ConfirmProvider>.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Löschen?',
 *     subtitle: 'Diese Aktion ist nicht rückgängig zu machen.',
 *     confirmLabel: 'Löschen',
 *     destructive: true,
 *   });
 *   if (ok) doDelete();
 */

const ConfirmCtx = createContext(null);

// ── ConfirmDialog — rendered once by ConfirmProvider ─────────────────────────
function ConfirmDialog({ dark, config, onResolve }) {
  const { height: vh, kbOpen, offsetTop } = useModalViewport();
  const [submitting, setSubmitting] = useState(false);
  if (!config) return null;

  const { title, subtitle, warning, confirmLabel, destructive } = config;
  const accentSolid = destructive ? 'var(--accent-red)' : 'var(--accent-online)';
  const accentWeak  = destructive ? 'rgba(255,69,58,0.14)' : 'rgba(55,138,221,0.14)';
  const textColor   = dark ? '#EBEBF5' : '#1C1C1E';
  const subColor    = dark ? 'rgba(235,235,245,0.6)' : '#636366';
  const mutedColor  = dark ? 'rgba(235,235,245,0.3)' : '#8E8E93';

  const handleConfirm = async () => {
    setSubmitting(true);
    onResolve(true);
  };

  const handleCancel = () => {
    if (!submitting) onResolve(false);
  };

  const shellStyle = modalShellStyle({
    vh, kbOpen, offsetTop, F, dark,
    width: 360, radius: 24, padding: '22px 22px 18px', zIndex: 2010,
  });

  return (
    <>
      <div
        onClick={handleCancel}
        style={{
          position: 'fixed', inset: 0, zIndex: 2009,
          background: dark ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.34)',
          backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.18s cubic-bezier(0.32, 0.72, 0, 1) both',
        }}
      />
      <div onClick={(e) => e.stopPropagation()} style={shellStyle}>
        <div style={{ fontSize: 17, fontWeight: 700, color: textColor, marginBottom: 4, textAlign: 'center' }}>
          {title}
        </div>
        {subtitle && (
          <div style={{ fontSize: 13, color: subColor, marginBottom: 12, textAlign: 'center', lineHeight: 1.5 }}>
            {subtitle}
          </div>
        )}
        {warning && (
          <div style={{
            background: accentWeak, color: accentSolid,
            borderRadius: 10, padding: '10px 12px', marginBottom: 14,
            fontSize: 12, lineHeight: 1.5, fontWeight: 500,
          }}>
            {warning}
          </div>
        )}
        <button
          onClick={handleConfirm}
          disabled={submitting}
          style={{
            width: '100%', padding: '13px',
            background: accentSolid, color: '#fff',
            border: 'none', borderRadius: 14,
            fontFamily: F, fontWeight: 700, fontSize: 14,
            cursor: submitting ? 'default' : 'pointer',
            opacity: submitting ? 0.6 : 1,
            marginBottom: 8,
          }}
        >
          {submitting ? '…' : (confirmLabel || 'Bestätigen')}
        </button>
        <button
          onClick={handleCancel}
          style={{
            width: '100%', padding: '10px',
            background: 'transparent', border: 'none',
            color: mutedColor, fontFamily: F, fontWeight: 500,
            fontSize: 13, cursor: 'pointer',
          }}
        >
          Abbrechen
        </button>
      </div>
    </>
  );
}

// ── ConfirmProvider ───────────────────────────────────────────────────────────
export function ConfirmProvider({ children, dark = false }) {
  const [config, setConfig] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setConfig(opts);
    });
  }, []);

  const handleResolve = useCallback((result) => {
    setConfig(null);
    resolverRef.current?.(result);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ConfirmDialog dark={dark} config={config} onResolve={handleResolve} />
    </ConfirmCtx.Provider>
  );
}

// ── useConfirm ────────────────────────────────────────────────────────────────
export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}
